export function roundToNearest(value: number, roundToNearestValue: number) {
  return Math.round(value / roundToNearestValue) * roundToNearestValue;
}

export function limitDecimals(value: number, numMaxDecimals: number = 2): number {
  return parseFloat(value.toFixed(numMaxDecimals));
}

interface RoundUpFactorOptions {
  mustIncrease: boolean;
}
export function roundUpToFactorOf(
  value: number,
  factor: number = 5,
  { mustIncrease }: RoundUpFactorOptions = { mustIncrease: false }
) {
  const alreadyFactorOf = value % factor === 0;
  if (alreadyFactorOf && !mustIncrease) {
    return value;
  }

  // +1 on value, if `mustIncrease` and value is already a factor of..
  const useValue = alreadyFactorOf && mustIncrease ? value + 1 : value;

  return (((useValue / factor) | 0) + 1) * factor;
}

export function percentOf(value: number = 0, total: number = 0, rounded: boolean = false): number {
  if (total === 0) {
    return 0;
  } // Division by 0 = NaN
  const percent = (value / total) * 100;
  if (rounded) {
    return Math.floor(percent);
  }
  return percent;
}

export function roundedPercentageOf(value: number, total: number): number {
  return Math.round(percentOf(value, total));
}

// ref https://www.jacklmoore.com/notes/rounding-in-javascript/
export function roundedValueToDecimalPlace(value: number, place: number): number {
  const num = value + 'e' + place;
  return +(Math.round(+num) + 'e-' + place);
}

// Pad a number
export function padNumber(value: number, width: number, padder: string = '0') {
  const num = `${value}`;
  return num.length >= width ? num : new Array(width - num.length + 1).join(padder) + num;
}

/**
 * Finds the median of an array of numbers in O(n log n) time.
 */
const findMedianNlog = (values: Array<number>) => {
  const count = values.length;
  if (count <= 0) return 0;

  values = [...values].sort((a, b) => a - b);
  const middle = Math.floor(count / 2);
  return count % 2 === 1 ? values[middle] : 0.5 * (values[middle - 1] + values[middle]);
};

/**
 * Finds the median of an array of numbers.
 */
export const findMedian = (values: Array<number>) => {
  return findMedianNlog(values);
};

/**
 * Exponential ease out function.
 * @param x Absolute progress between 0 and 1.
 * @returns Eased progress between 0 and 1.
 */
export const easeOutExpo = (x: number) => (x === 1 ? 1 : 1 - 2 ** (-10 * x));

export const humanize = (x: number, singular: string, plural: string): string =>
  x === 1 ? `one ${singular}` : `${x} ${plural}`;

/**
 * Reduce an array of objects to a single object, summing the values in every attribute.
 */
export function reduceBySummingAttributes<T extends { [key: string]: any }>(entries: Array<T>): T {
  return entries.reduce((totals, entry) => {
    Object.keys(entry).forEach((key: keyof T) => {
      if (typeof entry[key] === 'number') {
        totals[key] = ((totals[key] || 0) + entry[key]) as any;
      }
    });

    return totals;
  }, {} as T);
}

export function safeParseInt(value: string | null | undefined) {
  if (value === undefined || value === null) {
    return undefined;
  }

  const parsed = parseInt(value, 10);
  if (Number.isNaN(parsed)) {
    return undefined;
  }
  return parsed;
}

export const getSign = (value: number) => (Math.sign(value) === -1 ? '-' : '');
